import React, { useState, useEffect } from 'react';
import { CalendarOff, Plus, Trash2, Building2, Calendar, Clock, X, Edit2 } from 'lucide-react';
import { useLanguage } from './LanguageContext';
import { fetchWithAuth, getCurrentUser } from '../utils/api';

const DayOffManagement = () => {
  const { print_lang, currentLanguage } = useLanguage();
  const [dayOffs, setDayOffs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    start_date: '',
    end_date: '',
    text: '',
    branch_id: ''
  });
  const user = getCurrentUser();
  const branches = user?.branches || [];

  useEffect(() => {
    loadDayOffs();
  }, []);

  const loadDayOffs = async () => {
    try {
      const response = await fetchWithAuth('/api/dentists/my-day-offs');
      if (response.success) {
        setDayOffs(response.data);
      }
    } catch (error) {
      console.error('Failed to load day offs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingId
        ? `/api/dentists/day-off/${editingId}`
        : '/api/dentists/day-off';
      const method = editingId ? 'PUT' : 'POST';

      const response = await fetchWithAuth(url, {
        method,
        body: JSON.stringify(formData)
      });

      if (response.success) {
        setShowModal(false);
        setEditingId(null);
        setFormData({ start_date: '', end_date: '', text: '', branch_id: '' });
        loadDayOffs();
      }
    } catch (error) {
      console.error('Failed to save day off:', error);
      alert(print_lang('failed_to_save', 'Failed to save'));
    }
  };

  const handleEdit = (dayOff) => {
    setFormData({
      start_date: formatDateForInput(dayOff.start_date),
      end_date: formatDateForInput(dayOff.end_date),
      text: dayOff.text || '',
      branch_id: dayOff.branch_id || ''
    });
    setEditingId(dayOff.id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(print_lang('confirm_delete', 'Are you sure you want to delete this?'))) {
      return;
    }

    try {
      const response = await fetchWithAuth(`/api/dentists/day-off/${id}`, {
        method: 'DELETE'
      });

      if (response.success) {
        loadDayOffs();
      }
    } catch (error) {
      console.error('Failed to delete day off:', error);
      alert(print_lang('failed_to_delete', 'Failed to delete'));
    }
  };

  const formatDateForInput = (dateStr) => {
    const date = new Date(dateStr);
    return date.toISOString().slice(0, 16);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(currentLanguage === 'th' ? 'th-TH' : 'en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString(currentLanguage === 'th' ? 'th-TH' : 'en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Upcoming vs Past
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const upcomingDayOffs = dayOffs.filter(d => new Date(d.start_date) >= today);
  const pastDayOffs = dayOffs.filter(d => new Date(d.start_date) < today);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-800">
              {print_lang('my_day_offs', 'My Day Offs')}
            </h2>
            <p className="text-gray-500 mt-1">
              {print_lang('manage_your_day_offs', 'Manage your day off schedule')}
            </p>
          </div>
          <button
            onClick={() => {
              setFormData({ start_date: '', end_date: '', text: '', branch_id: '' });
              setEditingId(null);
              setShowModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            {print_lang('add_day_off', 'Add Day Off')}
          </button>
        </div>
      </div>

      {/* Upcoming Day Offs */}
      <div className="bg-white rounded-xl shadow-sm">
        <div className="p-4 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-600" />
            {print_lang('upcoming', 'Upcoming')} ({upcomingDayOffs.length})
          </h3>
        </div>

        {upcomingDayOffs.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <CalendarOff className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>{print_lang('no_upcoming_day_offs', 'No upcoming day offs')}</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {upcomingDayOffs.map(dayOff => (
              <div key={dayOff.id} className="p-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                      <CalendarOff className="w-6 h-6 text-gray-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">
                        {dayOff.text || print_lang('day_off', 'Day Off')}
                      </p>
                      <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(dayOff.start_date)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {formatTime(dayOff.start_date)} - {formatTime(dayOff.end_date)}
                        </span>
                      </div>
                      {dayOff.branch_name && (
                        <span className="inline-flex items-center gap-1 mt-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                          <Building2 className="w-3 h-3" />
                          {dayOff.branch_name}
                        </span>
                      )}
                      {!dayOff.branch_id && (
                        <span className="inline-flex items-center gap-1 mt-2 px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded-full">
                          {print_lang('all_branches', 'All Branches')}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleEdit(dayOff)}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(dayOff.id)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Past Day Offs */}
      {pastDayOffs.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm">
          <div className="p-4 border-b border-gray-100">
            <h3 className="text-lg font-semibold text-gray-500 flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              {print_lang('past', 'Past')} ({pastDayOffs.length})
            </h3>
          </div>
          <div className="divide-y divide-gray-100 opacity-60">
            {pastDayOffs.slice(0, 5).map(dayOff => (
              <div key={dayOff.id} className="p-4">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                    <CalendarOff className="w-5 h-5 text-gray-400" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-600">
                      {dayOff.text || print_lang('day_off', 'Day Off')}
                    </p>
                    <p className="text-sm text-gray-400">
                      {formatDate(dayOff.start_date)} - {formatTime(dayOff.start_date)} to {formatTime(dayOff.end_date)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 animate-slideUp">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">
                {editingId ? print_lang('edit_day_off', 'Edit Day Off') : print_lang('add_day_off', 'Add Day Off')}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="p-1 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {print_lang('start_date', 'Start Date/Time')} *
                </label>
                <input
                  type="datetime-local"
                  value={formData.start_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {print_lang('end_date', 'End Date/Time')} *
                </label>
                <input
                  type="datetime-local"
                  value={formData.end_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {print_lang('reason', 'Reason')}
                </label>
                <input
                  type="text"
                  value={formData.text}
                  onChange={(e) => setFormData(prev => ({ ...prev, text: e.target.value }))}
                  placeholder={print_lang('day_off', 'Day Off')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {print_lang('branch', 'Branch')} {editingId ? '*' : ''}
                </label>
                <select
                  value={formData.branch_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, branch_id: e.target.value }))}
                  required={!!editingId}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  {!editingId && (
                    <option value="">{print_lang('all_branches', 'All Branches')}</option>
                  )}
                  {branches.map(branch => (
                    <option key={branch.branch_id} value={branch.branch_id}>
                      {branch.branch_name}
                    </option>
                  ))}
                </select>
                {!editingId && (
                  <p className="text-xs text-gray-500 mt-1">
                    {print_lang('all_branches_hint', 'Select "All Branches" to create day-off for all your working branches')}
                  </p>
                )}
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {print_lang('cancel', 'Cancel')}
                </button>
                <button
                  type="submit"
                  className="flex-1 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {print_lang('save', 'Save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DayOffManagement;
